import React, { useState, useRef, useEffect } from 'react';
import {
    FaCloudDownloadAlt, FaTrashAlt, FaLock, FaFileAlt,
    FaUser, FaFolderOpen, FaCalendarAlt, FaLayerGroup,
    FaHistory, FaFilePdf, FaFileWord, FaFileImage, FaFileCsv,
    FaEdit, FaCloudUploadAlt, FaListUl, FaClock, FaShieldAlt
} from 'react-icons/fa';
import { Modal, CloseButton, Form, Button, ProgressBar, Badge } from 'react-bootstrap';
import { getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper";

// --- HELPERS ---
export const getCleanFileName = (url) => {
    if (!url) return "Attachment";
    try {
        const decodedUrl = decodeURIComponent(url);
        const fileNameWithParams = decodedUrl.split('/').pop();
        const fullFileName = fileNameWithParams.split('?')[0];
        const parts = fullFileName.split('_');
        return parts.length > 2 ? parts.slice(2).join('_') : fullFileName;
    } catch (error) { return "View Attachment"; }
};

const formatDate = (date) => {
    if (!date) return "N/A";
    const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getFileIcon = (url) => {
    const name = getCleanFileName(url).toLowerCase();
    if (name.endsWith('.pdf')) return <FaFilePdf className="text-danger" />;
    if (name.endsWith('.doc') || name.endsWith('.docx')) return <FaFileWord className="text-primary" />;
    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg')) return <FaFileImage className="text-warning" />;
    if (name.endsWith('.csv') || name.endsWith('.xlsx')) return <FaFileCsv className="text-success" />;
    return <FaFileAlt className="text-info" />;
};

// --- INTERNAL FORM COMPONENT (CREATE/UPDATE) ---
export function UpdateProfile({ item, theme, show, onHide, onSuccess, canDeleteAttachments }) {
    return <DocForm mode="update" item={item} theme={theme} show={show} onHide={onHide} onSuccess={onSuccess} canDeleteAttachments={canDeleteAttachments} />;
}

const DocForm = ({ mode = "create", item, theme, show, onHide, onSuccess, canDeleteAttachments, preSelectedRoleId }) => {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const storage = getStorage();
    const fileInputRef = useRef(null);

    const [docId, setDocId] = useState(item?.id || null);
    const [isCreating, setIsCreating] = useState(false);
    const [isEditingInfo, setIsEditingInfo] = useState(mode === 'create');
    const [showHistory, setShowHistory] = useState(false);
    const [progress, setProgress] = useState(0);
    const [formData, setFormData] = useState(item || {
        name: '', details: '', type: '', expirationDate: '', isPrivate: false, fileURLs: [], activityLog: []
    });

    useEffect(() => {
        if (show && item) {
            setFormData(item);
            setDocId(item.id);
            if (mode === 'update' && item.fileURLs?.length > 0) {
                setIsEditingInfo(false);
            }
        } else if (show && mode === 'create') {
            setFormData({ name: '', details: '', type: '', expirationDate: '', isPrivate: false, fileURLs: [], activityLog: [] });
            setDocId(null);
            setIsEditingInfo(true);
        }
    }, [show, item, mode]);

    const styles = {
        backdrop: isDark ? "bg-dark" : "bg-light",
        text: isDark ? 'text-white' : 'text-dark',
        input: isDark ? 'glass-input mb-3' : 'form-control border-light-subtle  mb-3',
        tray: { background: isDark ? 'rgba(255,255,255,0.05)' : '#f8f9fa' }
    };

    const handleSaveInfo = async () => {
        if (!formData.name.trim() || !formData.type) return toast.error("Title and Category required");
        setIsCreating(true);
        const user = getAuth().currentUser;
        const userName = user?.displayName || user?.email || "Anonymous";

        try {
            let changedFields = [];
            if (docId && item) {
                if (item.name !== formData.name) changedFields.push("Title");
                if (item.type !== formData.type) changedFields.push("Category");
                if (item.details !== formData.details) changedFields.push("Notes");
                if (item.expirationDate !== formData.expirationDate) changedFields.push("Expiration");
                if (item.isPrivate !== formData.isPrivate) changedFields.push("Privacy Status");
            }

            const logEntry = {
                user: userName,
                action: mode === 'create' ? "Created Record" : `Modified: ${changedFields.join(', ')}`,
                timestamp: new Date()
            };

            const cleanData = {
                name: formData.name,
                details: formData.details,
                type: formData.type,
                expirationDate: formData.expirationDate,
                isPrivate: formData.isPrivate,
                updated: new Date(),
                lastModifiedBy: userName,
                activityLog: arrayUnion(logEntry)
            };

            if (docId) {
                await updateDoc(doc(db, "docs", docId), cleanData);
                setIsEditingInfo(false);
                toast.success("Info Updated");
            } else {
                const initialRoles = preSelectedRoleId ? [preSelectedRoleId] : [];
                
                const docRef = await addDoc(collection(db, "docs"), {
                    ...cleanData,
                    fileURLs: [],
                    associatedRoles: initialRoles,
                    status: 'Draft',
                    createdBy: userName,
                    created: new Date(),
                    activityLog: [logEntry]
                });

                // Bidirectional Target Fix: Explicitly appends new doc ID into the role document's array
                if (preSelectedRoleId) {
                    await updateDoc(doc(db, "roles", preSelectedRoleId), {
                        associatedDocs: arrayUnion(docRef.id)
                    });
                }

                setDocId(docRef.id);
                setIsEditingInfo(false);
                toast.success("Record Initialized");
            }
            if (onSuccess) onSuccess();
        } catch (err) { toast.error(err.message); }
        finally { setIsCreating(false); }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !docId) return;
        const user = getAuth().currentUser;
        const userName = user?.displayName || user?.email || "Anonymous";

        const uniqueName = `${Date.now()}_${file.name}`;
        const fileRef = ref(storage, `files/${uniqueName}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask.on('state_changed',
            (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            (err) => { toast.error(err.message); setProgress(0); },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                const logEntry = {
                    user: userName,
                    action: `Attached file: ${file.name}`,
                    timestamp: new Date()
                };

                const updatePayload = {
                    fileURLs: arrayUnion(url),
                    status: 'Published',
                    updated: new Date(),
                    lastModifiedBy: userName,
                    activityLog: arrayUnion(logEntry)
                };

                await updateDoc(doc(db, "docs", docId), updatePayload);
                setFormData(prev => ({
                    ...prev,
                    fileURLs: [...(prev.fileURLs || []), url],
                    updated: new Date(),
                    lastModifiedBy: userName,
                    activityLog: [...(prev.activityLog || []), logEntry]
                }));
                setProgress(0);
                toast.success("File Added");
                if (onSuccess) onSuccess();
            }
        );
    };

    const removeFile = async (url) => {
        if (!canDeleteAttachments) return toast.error("Unauthorized to purge record assets.");
        if (!window.confirm("Remove attachment?")) return;
        const user = getAuth().currentUser;
        const userName = user?.displayName || user?.email || "Anonymous";
        const fileName = getCleanFileName(url);

        try {
            await deleteObject(ref(storage, url)).catch(() => { });
            const logEntry = {
                user: userName,
                action: `Deleted file: ${fileName}`,
                timestamp: new Date()
            };

            const updatePayload = {
                fileURLs: arrayRemove(url),
                updated: new Date(),
                lastModifiedBy: userName,
                activityLog: arrayUnion(logEntry)
            };

            await updateDoc(doc(db, "docs", docId), updatePayload);
            setFormData(prev => ({
                ...prev,
                fileURLs: prev.fileURLs.filter(u => u !== url),
                updated: new Date(),
                lastModifiedBy: userName,
                activityLog: [...(prev.activityLog || []), logEntry]
            }));
            toast.info("File Removed");
            if (onSuccess) onSuccess();
        } catch (err) { toast.error(err.message); }
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static" backdropClassName={styles.backdrop}>
            <Modal.Header className="border-bottom border-opacity-10">
                <Modal.Title className={`fw-bold ${styles.text}`}>
                    {mode === 'create' ? 'Create Record' : 'Update Record'}
                </Modal.Title>
                <div className="ms-auto d-flex align-items-center gap-3">
                    {docId && (
                        <Button variant="link" className={`p-0 transition-hover ${showHistory ? 'text-info' : 'text-white-50'}`} onClick={() => setShowHistory(!showHistory)}>
                            <FaListUl size={18} />
                        </Button>
                    )}
                    <CloseButton variant={isDark ? "white" : undefined} onClick={onHide} />
                </div>
            </Modal.Header>
            <Modal.Body className="p-4 custom-scrollbar" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {showHistory && (
                    <div className="mb-4 animated fadeIn">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="bg-info bg-opacity-10 p-2 rounded-circle"><FaHistory className="text-info" size={14} /></div>
                            <h6 className="m-0 fw-bold text-white-50 small text-uppercase tracking-wider">Activity History</h6>
                        </div>
                        <div className="activity-timeline ps-3 border-start border-white border-opacity-10 ms-2">
                            {formData.activityLog?.slice().reverse().map((log, i) => (
                                <div key={i} className="activity-item position-relative mb-4">
                                    <div className="timeline-dot bg-info position-absolute" style={{ width: '8px', height: '8px', borderRadius: '50%', left: '-17px', top: '6px' }}></div>
                                    <div className="d-flex justify-content-between align-items-start mb-0">
                                        <span className="fw-bold text-info" style={{ fontSize: '13px' }}>{log.user}</span>
                                        <span className="text-white-50 d-flex align-items-center gap-1" style={{ fontSize: '10px' }}><FaClock size={8} /> {formatDate(log.timestamp)}</span>
                                    </div>
                                    <div className="small text-white opacity-75 mt-1">{log.action}</div>
                                </div>
                            ))}
                        </div>
                        <Button variant="outline-secondary" size="sm" className="w-100 border-opacity-25 py-2 mt-2" onClick={() => setShowHistory(false)}>Hide History</Button>
                    </div>
                )}

                {!showHistory && (
                    <>
                        {docId && (
                            <div className="mb-3 border-bottom border-opacity-10 pb-2 animated fadeIn">
                                <div className="d-flex justify-content-between w-100">
                                    <div className={`${isDark ? 'text-white-50' : 'text-muted'} small`}><FaUser className="text-info me-1" size={10} />Create by: {formData.createdBy || 'System'}</div>
                                    <div className={`${isDark ? 'text-white-50' : 'text-muted'} small`}><FaHistory className="text-warning me-1" size={10} />Modified: {formatDate(formData.updated || formData.created)}</div>
                                </div>
                            </div>
                        )}

                        <div style={{ display: isEditingInfo ? 'block' : 'none' }}>
                            <label className="text-info small fw-bold mb-1">METADATA</label>
                            <input type="text" className={styles.input} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Title..." />
                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <select className={styles.input} value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                                        <option value="">Category</option>
                                        <option value="Legal">Legal</option>
                                        <option value="Financial">Financial</option>
                                        <option value="Meeting Minutes">Meeting Minutes</option>
                                    </select>
                                </div>
                                <div className="col-6"><input type="date" className={styles.input} value={formData.expirationDate} onChange={e => setFormData({ ...formData, expirationDate: e.target.value })} /></div>
                            </div>
                            <Form.Check type="switch" label="Private Record" className={`mb-3 ${styles.text}`} checked={formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })} />
                            <textarea className={styles.input} rows="2" value={formData.details} onChange={e => setFormData({ ...formData, details: e.target.value })} placeholder="Notes..." />
                            <Button variant="info" className="w-100 mb-2 text-white fw-bold" onClick={handleSaveInfo} disabled={isCreating}>{isCreating ? "Saving..." : docId ? "Update Metadata" : "Initialize & Continue"}</Button>
                            {docId && mode === 'update' && <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => setIsEditingInfo(false)}>Back to Attachments</Button>}
                        </div>

                        {docId && !isEditingInfo && (
                            <div className="mt-2 animated fadeIn">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="text-info small fw-bold">ATTACHMENTS</label>
                                    <Button variant="link" className="text-info p-0 text-decoration-none d-flex align-items-center gap-1" onClick={() => setIsEditingInfo(true)} style={{ fontSize: '11px' }}><FaEdit size={12} /> Edit Info</Button>
                                </div>
                                <div className="p-2 rounded mb-3 border border-opacity-10" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#f8f9fa' }}>
                                    {formData.fileURLs?.length > 0 ? (
                                        formData.fileURLs.map((url, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center mb-1 p-2 border-bottom border-opacity-10">
                                                <span className={`small text-truncate ${isDark ? 'text-white-50' : 'text-muted'}`}>{getFileIcon(url)} <span className="ms-2">{getCleanFileName(url)}</span></span>
                                                {canDeleteAttachments && <button className="btn btn-sm text-danger p-0 ms-2" onClick={() => removeFile(url)}><FaTrashAlt size={12} /></button>}
                                            </div>
                                        ))
                                    ) : <div className="text-center py-3 text-muted small">No files attached yet</div>}
                                    {progress > 0 && (
                                        <div className="px-2 mb-2">
                                            <div className="d-flex justify-content-between small text-info mb-1"><span>Uploading...</span><span>{progress}%</span></div>
                                            <ProgressBar variant="info" now={progress} size="sm" style={{ height: '4px' }} />
                                        </div>
                                    )}
                                    <div className="mt-2">
                                        <input type="file" className="d-none" ref={fileInputRef} onChange={handleFileUpload} />
                                        <Button variant={isDark ? "outline-info" : "info"} className={`w-100 py-2 d-flex align-items-center justify-content-center gap-2 ${!isDark && 'text-white'}`} onClick={fileInputRef.current ? () => fileInputRef.current.click() : undefined} disabled={progress > 0}><FaCloudUploadAlt /> Upload New Attachment</Button>
                                    </div>
                                </div>
                                <Button variant="success" className="w-100 fw-bold py-2 shadow-sm" onClick={onHide}>Complete Record</Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export function CreateDoc({ theme, onSuccess, preSelectedRoleId, buttonLabel = "+ New Document" }) {
    const [show, setShow] = useState(false);
    return (
        <>
            <button className="btn-glass-create text-truncate" style={{ fontSize: '11px', padding: '5px 10px' }} onClick={() => setShow(true)}>
                {buttonLabel}
            </button>
            <DocForm 
                mode="create" 
                theme={theme} 
                show={show} 
                onHide={() => setShow(false)} 
                onSuccess={onSuccess} 
                preSelectedRoleId={preSelectedRoleId} 
            />
        </>
    );
}

export default function XDocList(props) {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';
    const db = getFirestore();
    const storage = getStorage();

    const [showSummary, setShowSummary] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [linkedRoleNames, setLinkedRoleNames] = useState([]);

    const canEdit = props.isAdmin === true;

    useEffect(() => {
        if (showSummary && props.associatedRoles?.length > 0) {
            getDocs(collection(db, "roles")).then((snapshot) => {
                const names = [];
                snapshot.docs.forEach(docSnap => {
                    if (props.associatedRoles.includes(docSnap.id)) {
                        names.push(docSnap.data().roleName);
                    }
                });
                setLinkedRoleNames(names);
            }).catch(err => console.error(err));
        } else if (showSummary) {
            setLinkedRoleNames([]);
        }
    }, [showSummary, props.associatedRoles, db]);

    if (props.public && props.isPrivate) return null;

    const lastModified = props.updated || props.created;

    const confirmDelete = async () => {
        if (!props.canDeleteAttachments) return toast.error("Unauthorized to wipe file clusters.");
        if (window.confirm("Delete document and all attachments?")) {
            spinner(true);
            try {
                const urls = [...(props.fileURLs || [])];
                await Promise.all(urls.map(url => deleteObject(ref(storage, url)).catch(() => { })));
                await deleteDoc(doc(db, "docs", props.id));
                toast.success("Deleted");
                props.onDeleteSuccess();
            } catch (err) { toast.error(err.message); }
            finally { spinner(false); }
        }
    };

    const cardStyles = {
        card: isDark ? 'doc-card animated fadeIn mb-2 p-0 overflow-hidden shadow-sm' : 'card mb-2 border-0 shadow-sm overflow-hidden',
        header: { background: isDark ? 'rgba(8, 8, 8, 0)' : 'linear-gradient(45deg, rgb(173, 179, 179), rgb(175, 189, 230))', borderBottom: isDark ? '1px solid rgba(255,255,255,0.03)' : '1px solid #dee2e600' },
        title: isDark ? 'text-white' : 'text-dark',
        body: isDark ? 'p-3' : 'p-3 bg-grey',
        modal: isDark ? 'bg-dark' : 'bg-light'
    };

    return (
        <>
            <div className={cardStyles.card} style={isDark ? { border: '1px solid rgba(121, 121, 121, 0.31)' } : {}}>
                <div className="card-action-header d-flex justify-content-start align-items-center p-2" style={cardStyles.header}>

                    <div className="d-flex gap-2">
                        
                        <button onClick={() => setShowSummary(true)} className="circle-btn lgreen"><FaFolderOpen size={14} /></button>
                        {canEdit && <button onClick={() => setShowUpdate(true)} className="circle-btn"><FaEdit size={14} /></button>}
                        {props.canDeleteAttachments && <button onClick={confirmDelete} className="circle-btn centerCircle btn-delete"><FaTrashAlt size={12} /></button>}
                       
                    </div>
                </div>

                <div className={cardStyles.body}>
                    <div className="mb-2 d-flex align-items-start gap-2">
                        <h5 className={`m-0 fw-bold ${cardStyles.title}`} style={{ fontSize: '1.1rem' }}>{props.name}</h5>
                        {props.isPrivate && <FaLock size={12} className="text-success mt-1" />}
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-info small">📅 {formatDate(props.created)}</span>
                          <span className={isDark ? "text-white-50" : "text-muted blue"} style={{ fontSize: '10px' }}><FaHistory size={8} className="me-1" /> {formatDate(lastModified)}</span>
                     
                    </div>
                </div>
            </div>

            <Modal show={showSummary} onHide={() => setShowSummary(false)} centered backdropClassName={cardStyles.modal} >
                <Modal.Body className={`p-4 ${isDark ? 'bd-dark' : 'bg-light'}`}>
                    <div className="mb-4 p-3 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#f8f9fa', border: isDark ? '1px solid #ffffff14' : '1px solid #dee2e6' }}>
                        <div className="d-flex justify-content-between align-items-start">
                            <h2 className={cardStyles.title + " fw-bold mb-1"} style={{ fontSize: '1.3rem' }}>{props.name}</h2>
                            <span className={`badge ${props.isPrivate ? 'bg-success' : 'bg-info'} bg-opacity-8 border border-opacity-8`}>{props.isPrivate ? "Private" : "Public"}</span>
                        </div>
                        <span className="text-info small fw-bold text-uppercase"><FaLayerGroup className="me-1" /> {props.type || "General"}</span>
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-6">
                            <div className="p-2 rounded border border-opacity-10 h-100" style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#fff' }}>
                                <label className="text-info d-block mb-1" style={{ fontSize: '10px', fontWeight: 'bold' }}>EXPIRATION</label>
                                <div className={`${cardStyles.title} small`}><FaCalendarAlt className="text-warning me-1" /> {props.expirationDate|| "Indefinite"}</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="p-2 rounded border border-opacity-10 h-100" style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#fff' }}>
                                <label className="text-info d-block mb-1" style={{ fontSize: '10px', fontWeight: 'bold' }}>OWNER</label>
                                <div className={`${cardStyles.title} small text-truncate`}><FaUser className="text-info me-1" /> {props.createdBy || "System"}</div>
                            </div>
                        </div>
                    </div>

                    <label className="text-info small fw-bold mb-2"><FaShieldAlt size={10} className="me-1" /> AUTHORIZED GROUPS</label>
                    <div className="mb-4 d-flex gap-1 flex-wrap">
                        {linkedRoleNames.length > 0 ? linkedRoleNames.map((roleName, idx) => (
                            <Badge key={idx} bg="dark" className="border border-secondary px-2 py-1 small text-white opacity-75">👥 {roleName}</Badge>
                        )) : <span className="text-muted small ps-1" style={{ fontSize: '12px' }}>Open Access (No role constraints)</span>}
                    </div>

                    <label className="text-info small fw-bold mb-2">NOTES</label>
                    <div className={`p-3 rounded mb-4 ${isDark ? 'text-white-50' : 'text-muted'}`} style={{ background: isDark ? 'rgba(255,255,255,0.02)' : '#f8f9fa', border: '1px dashed #dee2e6' }}>{props.details || "No notes."}</div>

                    <label className="text-info small fw-bold mb-2">FILES</label>
                    <div className="rounded mb-4 overflow-hidden border">
                        {props.fileURLs?.length > 0 ? props.fileURLs.map((url, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                <span className={`small text-truncate ${cardStyles.title}`}>{getFileIcon(url)} {getCleanFileName(url)}</span>
                                <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm text-info"><FaCloudDownloadAlt size={16} /></a>
                            </div>
                        )) : <div className="p-3 text-center text-muted small">No files.</div>}
                    </div>
                    
                    <div className="d-flex gap-2">
                        <Button variant="secondary" className="w-50 fw-bold py-2" onClick={() => setShowSummary(false)}>CLOSE</Button>
                        {canEdit && <Button variant="info" className="w-50 text-white fw-bold py-2" onClick={() => { setShowSummary(false); setShowUpdate(true); }}><FaEdit className="me-1" /> EDIT FILE</Button>}
                    </div>
                </Modal.Body>
            </Modal>

            <DocForm mode="update" item={props} theme={theme} show={showUpdate} onHide={() => setShowUpdate(false)} onSuccess={props.onDeleteSuccess} canDeleteAttachments={props.canDeleteAttachments} />
        </>
    );
}